import React, { useState, useEffect, useCallback, useMemo } from 'react'
import ReactTable from "react-table";
import { decimalAdjust } from "../../Global";
import { EditableNumericInput, EditableAlphanumericInput, EditableDateInput, EditableSelect } from './Inputs';

function IngresoTable(props) {
    const [rows, setRows] = useState([]);

    useEffect(() => {
        if (props.data) {
            setRows(props.data.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())));
        }
    }, [props.data]);

    const displayedRows = rows;

    const handleValueChange = useCallback((item, fieldName, newValue) => {
        if (props.handleValueChange) {
            props.handleValueChange(item, fieldName, newValue);
        }
    }, [props.handleValueChange]);

    const handleDelete = useCallback((item) => {
        if (props.handleDelete) {
            props.handleDelete(item);
        }
    }, [props.handleDelete]);

    const calculateTotal = useCallback((cantidad, precioCosto) => {
        return decimalAdjust('floor', cantidad * precioCosto, -2);
    }, []);

    const calculateUtilidadBruta = useCallback((cantidad, precioVenta, precioCosto, bonificado = 0) => {
        const totalVenta = cantidad * precioVenta;
        const totalCosto = cantidad * precioCosto;
        const bonificadoCosto = bonificado * precioCosto;
        return decimalAdjust('floor', totalVenta - totalCosto - bonificadoCosto, -2);
    }, []);

    const columns = useMemo(() => [
        {
            Header: "",
            accessor: "action",
            maxWidth: 80,
            Cell: row => (
                <div style={{ textAlign: "center" }}>
                    <button
                        onClick={() => handleDelete(row.original)}
                        style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
                        title="Eliminar item"
                    >
                        ✕
                    </button>
                </div>
            )
        },
        {
            Header: "Producto",
            accessor: "label",
            minWidth: 250,
            Cell: row => (
                <div style={{ fontWeight: 'bold' }}>
                    {row.value}
                </div>
            )
        },
        {
            Header: "Tipo Transacción",
            accessor: "idTipoTransaccion",
            width: 150,
            Cell: row => (
                <div style={{ fontWeight: 'bold'}}>
                    {props.tiposTransaccion.find(x => x.IdTipoTransaccionAlmacen == row.value)?.DescripcionCorta}
                </div>
            )
        },
        {
            Header: "Cantidad",
            accessor: "Cantidad",
            maxWidth: 100,
            Cell: row => (
                <div style={{ textAlign: "right" }}>
                    <EditableNumericInput
                        item={row.original}
                        initialValue={row.value}
                        onValueChange={handleValueChange}
                        fieldName="Cantidad"
                        step="1"
                        min="0"
                    />
                </div>
            )
        },
        {
            Header: "Cod. Lote",
            accessor: "CodLote",
            maxWidth: 100,
            Cell: row => (
                <div style={{ textAlign: "right" }}>
                    <EditableAlphanumericInput
                        className="text-uppercase"
                        item={row.original}
                        initialValue={row.value || row.original.CodigoLote}
                        onValueChange={handleValueChange}
                        fieldName="CodLote"
                    />
                </div>
            )
        },
        {
            Header: "F. Vencimiento",
            accessor: "FechaVencimiento",
            width: 150,
            Cell: row => (
                <EditableDateInput
                    item={row.original}
                    initialValue={row.value || row.original.FechaVencimientoLote}
                    onValueChange={handleValueChange}
                    fieldName="FechaVencimiento"
                />
            )
        },
        {
            Header: "Porcentaje",
            accessor: "Porcentaje",
            maxWidth: 100,
            Cell: row => {
                if (+row.original.idTipoTransaccion === 7) {
                    return (
                        <div style={{ textAlign: "right", fontWeight: 'bold' }}>
                        </div>
                    );
                }

                const porcentaje = row.original.PrecioCosto && row.original.PrecioCosto !== 0
                    ? Number((((row.original.PrecioVenta - row.original.PrecioCosto) / row.original.PrecioCosto) * 100).toFixed(2))
                    : 0;
                return (
                    <div style={{ textAlign: "right" }}>
                        {porcentaje} %
                    </div>
                )
            }
        },
        {
            Header: "% Flete",
            accessor: "PorcentajeFlete",
            maxWidth: 100,
            show: !!props.compraExterna,
            Cell: row => {
                let porcentaje = 0;
                if (props.compraExterna) {
                    const totalCantidad = props.data.reduce((acc, item) => acc + (Number(item.Cantidad) || 0), 0);
                    const cantidadActual = Number(row.original.Cantidad) || 0;
                    porcentaje = totalCantidad > 0 ? ((cantidadActual / totalCantidad) * 100).toFixed(2) : 0;
                }
                return (
                    <div style={{ textAlign: "right" }}>
                        {porcentaje} %
                    </div>
                )
            }
        },
        {
            Header: "P. Costo",
            accessor: "PrecioCosto",
            maxWidth: 100,
            show: !props.confEmpresa.movAlmacenJustPrecioVenta,
            Cell: row => (
                +row.original.idTipoTransaccion !== 7 && (
                <div style={{ textAlign: "right" }}>
                    {
                        !props.puedeModificarPrecioCosto ? (
                            <div style={{ textAlign: "right" }}>
                                {row.value || 0}
                            </div>
                        ) : <EditableNumericInput
                            item={row.original}
                            initialValue={row.value || 0}
                            onValueChange={handleValueChange}
                            disabled={props.confEmpresa.movAlmacenJustPrecioVenta}
                            fieldName="PrecioCosto"
                            step="0.01"
                            min="0"
                        />
                    }
                </div>
            ))
        },
        {
            Header: "P. Venta",
            accessor: "PrecioVenta",
            maxWidth: 100,
            Cell: row => (
                +row.original.idTipoTransaccion !== 7 && (
                <div style={{ textAlign: "right" }}>
                    {
                        !props.puedeModificarPrecioVenta ? (
                            <div style={{ textAlign: "right" }}>
                                {row.value || 0}
                            </div>
                        ) : <EditableNumericInput
                            item={row.original}
                            initialValue={row.value || 0}
                            onValueChange={handleValueChange}
                            fieldName="PrecioVenta"
                            step="0.01"
                            min="0"
                        />
                    }
                </div>
            ))
        },
        {
            Header: "Total",
            accessor: "Total",
            maxWidth: 100,
            Cell: row => {
                if (+row.original.idTipoTransaccion == 7) {
                    return (
                        <div style={{ textAlign: "right", fontWeight: 'bold' }}>
                        </div>
                    );
                }

                
                const cantidad = row.original.Cantidad || 0;

                let precio = row.original.PrecioCosto || 0;


                if (props.confEmpresa.movAlmacenJustPrecioVenta) {
                    precio = row.original.PrecioVenta || 0;
                }

                const total = calculateTotal(cantidad, precio);

                return (
                    <div style={{ textAlign: "right", fontWeight: 'bold' }}>
                        {total}
                    </div>
                );
            }
        },
        {
            Header: "U. BRUTA",
            accessor: "UtilidadBruta",
            maxWidth: 100,
            Cell: row => {
                if (row.original.idTipoTransaccion === 7) {
                    return (
                        <div style={{ textAlign: "right", fontWeight: 'bold' }}>
                        </div>
                    );
                }

                const cantidad = row.original.Cantidad || 0;
                const precioVenta = row.original.PrecioVenta || 0;
                const precioCosto = row.original.PrecioCosto || 0;
                const bonificado = row.original.Bonificado || 0;
                const utilidadBruta = calculateUtilidadBruta(cantidad, precioVenta, precioCosto, bonificado);
                return (
                    <div style={{ 
                        textAlign: "right", 
                        fontWeight: 'bold',
                        color: utilidadBruta >= 0 ? '#28a745' : '#dc3545'
                    }}>
                        {utilidadBruta}
                    </div>
                );
            }
        }
    ], [handleValueChange, calculateTotal, calculateUtilidadBruta, handleDelete]);

    return (
        <div>
            <ReactTable
                data={displayedRows}
                disabled={false}
                columns={columns}
                defaultPageSize={Math.max(displayedRows.length, 1)}
                pageSize={displayedRows.length}
                showPagination={false}
                style={{
                    height: "auto",
                    width: "100%",
                    fontSize: "13px"
                }}
                className="-striped -highlight"
                minRows={0}
            />
        </div>
    );
}

export default IngresoTable;
